import React, { useState } from 'react';

function MainMenu({ onNewGame, onContinue, hasSave }) {
  const [showBook, setShowBook] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);

  // Book chapters - condensed story (4 pages)
  const chapters = [
    {
      title: "The Exile's Chronicle",
      subtitle: "The Fall",
      content: (
        <>
          <p className="book-intro">
            You were once a ruler of unmatched wisdom, governing a prosperous realm
            that spanned across the stars. Peace reigned for a thousand years.
          </p>
          <p>
            But power breeds envy. A coalition of jealous lords, wielding forbidden
            <span className="book-highlight"> dimensional magic</span>, conspired against you.
            In a flash of blinding light, you were torn from your throne and cast
            through the fabric of reality itself.
          </p>
          <p>
            You awaken now in a primitive world of wood and stone. Your crown is gone,
            your armies scattered across infinity. But your mind remains sharp, and
            your will unbroken.
          </p>
          <div className="book-seal">👑</div>
        </>
      ),
    },
    {
      title: "Chapter I",
      subtitle: "The Path Home",
      content: (
        <>
          <p>
            Ancient texts speak of the <span className="book-highlight">Dimensional Beacon</span> —
            a device capable of piercing the veil between universes.
          </p>
          <p>
            To construct it, you must advance through 11 eras of this primitive world.
            Master forgotten sciences. Harness arcane energies. Build an empire capable
            of gathering the resources and knowledge required.
          </p>

          <div className="book-tree-preview">
            <div className="tree-era">Era I<br/>🪓 Stone Age</div>
            <div className="tree-arrow">→</div>
            <div className="tree-era">Era VI<br/>⚙️ Industry</div>
            <div className="tree-arrow">→</div>
            <div className="tree-era">Era XI<br/>🌌 Transcendence</div>
          </div>

          <p className="book-tip">
            💡 <em>Your era is based on your highest researched tech tier.
            Watch your progress bar at the top!</em>
          </p>
        </>
      ),
    },
    {
      title: "Chapter II",
      subtitle: "Building an Empire",
      content: (
        <>
          <p>
            This world offers resources ranging from basic materials to mystical artifacts.
            Build structures to automate production. Research technologies to unlock new tiers.
            Hire advisors for powerful bonuses.
          </p>

          <div className="book-mechanic">
            <div className="mech-icon">🏗️</div>
            <div className="mech-content">
              <h4>Core Systems</h4>
              <ul>
                <li><strong>Buildings</strong> — Produce resources per second (costs increase)</li>
                <li><strong>Research</strong> — Unlock new buildings, resources, and abilities</li>
                <li><strong>Advisors</strong> — Provide bonuses with trade-offs (2-5 slots)</li>
                <li><strong>Wonders</strong> — Massive permanent global bonuses</li>
                <li><strong>World Map</strong> — Explore hex tiles for gathering and expeditions</li>
              </ul>
            </div>
          </div>

          <p className="book-tip">
            💡 <em>Follow quests for guided progression. Press 📖 Guide for detailed help.</em>
          </p>
        </>
      ),
    },
    {
      title: "Chapter III",
      subtitle: "Your Journey Starts Now",
      content: (
        <>
          <p className="book-intro">
            You now possess the knowledge needed to rebuild. The path will be long,
            but every great empire began with a single building.
          </p>

          <div className="book-summary">
            <h4>Quick Start:</h4>
            <ol>
              <li><strong>Build</strong> Woodcutters, Farms, and Stone Pickers (5-10 each)</li>
              <li><strong>Research</strong> technologies to unlock new tiers</li>
              <li><strong>Explore</strong> the world map and gather resources</li>
              <li><strong>Follow quests</strong> for rewards and guidance</li>
              <li><strong>Construct the Dimensional Beacon</strong> to return home</li>
            </ol>
          </div>

          <p className="book-final">
            Go forth, Exiled One. Your throne awaits across the dimensional void.
          </p>

          <div className="book-seal large">⚔️</div>
        </>
      ),
    },
  ];

  const chapter = chapters[currentChapter];
  const isFirstChapter = currentChapter === 0;
  const isLastChapter = currentChapter === chapters.length - 1;

  const handlePrev = () => {
    if (currentChapter > 0) setCurrentChapter(currentChapter - 1);
  };

  const handleNext = () => {
    if (currentChapter < chapters.length - 1) {
      setCurrentChapter(currentChapter + 1);
    } else {
      onNewGame();
    }
  };

  // Book/Chronicle view
  if (showBook) {
    return (
      <div className="main-menu book-mode">
        <div className="book-container">
          {/* Book binding decoration */}
          <div className="book-spine"></div>
          
          {/* Left page - chapter list */}
          <div className="book-page book-left">
            <div className="page-header">
              <span className="page-ornament">❧</span>
              <span>Contents</span>
              <span className="page-ornament">❧</span>
            </div>
            <div className="chapter-list">
              {chapters.map((ch, i) => (
                <div 
                  key={i}
                  className={`chapter-item ${i === currentChapter ? 'active' : ''} ${i < currentChapter ? 'read' : ''}`}
                  onClick={() => setCurrentChapter(i)}
                >
                  <span className="ch-marker">{i === 0 ? '📖' : `${i}.`}</span>
                  <span className="ch-title">{ch.subtitle}</span>
                </div>
              ))}
            </div>
            <div className="page-number">—</div>
          </div>
          
          {/* Right page - content */}
          <div className="book-page book-right">
            <div className="page-header">
              <span className="page-ornament">❧</span>
              <span>{chapter.title}</span>
              <span className="page-ornament">❧</span>
            </div>
            <h2 className="chapter-title">{chapter.subtitle}</h2>
            <div className="chapter-content">
              {chapter.content}
            </div>
            <div className="page-number">{currentChapter + 1}</div>
          </div>
          
          {/* Navigation */}
          <div className="book-nav">
            <button 
              className="book-nav-btn" 
              onClick={handlePrev}
              disabled={isFirstChapter}
            >
              ← Previous
            </button>
            <button className="book-skip-btn" onClick={onNewGame}>
              Skip to Game
            </button>
            <button className="book-nav-btn primary" onClick={handleNext}>
              {isLastChapter ? '⚔️ Begin Journey' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main menu view
  return (
    <div className="main-menu">
      <div className="menu-container">
        <div className="menu-header">
          <div className="menu-icon">⚔️</div>
          <h1 className="menu-title">Civ-IDLE</h1>
          <p className="menu-subtitle">Build Your Civilization, One Click at a Time</p>
        </div>

        <div className="menu-tagline">
          <span className="tagline-icon">👑</span>
          <p>An exiled ruler. A strange new world. One chance to return home.</p>
        </div>

        <div className="menu-buttons">
          <button className="menu-btn primary" onClick={() => setShowBook(true)}>
            <span className="btn-icon">📖</span>
            <span className="btn-text">New Game</span>
          </button>
          
          {hasSave && (
            <button className="menu-btn secondary" onClick={onContinue}>
              <span className="btn-icon">▶️</span>
              <span className="btn-text">Continue</span>
            </button>
          )}
        </div>

        <div className="menu-footer">
          <p>Build • Research • Ascend • Return</p>
        </div>
      </div>

      <div className="menu-bg-effects">
        <div className="star star-1">✦</div>
        <div className="star star-2">✧</div>
        <div className="star star-3">✦</div>
        <div className="star star-4">✧</div>
        <div className="star star-5">⚝</div>
      </div>
    </div>
  );
}

export default MainMenu;
